import React, { useEffect, useRef, useState } from "react";
import { BiPlus } from "react-icons/bi";
import { HiOutlineAdjustmentsHorizontal } from "react-icons/hi2";
import {
  ALL_FORM_LIST_LIMIT,
  formTemplateStatusDefault,
  formTemplateStatusOptions,
  LOADER_STATUS,
  SORT_TYPE,
  USER_ROLE,
} from "../../../Constants/constant";
import CircularLoader from "../../../components/common/CircularLoader/CircularLoader";
import Search from "../../../components/common/Search/Search";
import SelectOption from "../../../components/common/Select/Select";
import TableHeader from "../../../components/common/Table/TableHeader";
import Header from "../../../components/common/Header/Header";
import { useNavigate } from "react-router";
import { roleAccess } from "helpers/RolesPermission";
import PaginationComponent from "components/common/Pagination/index";
import FormsListActionButtons from "./FormsListActionButtons";
import { PERMISSION_KEYS, PERMISSION_KEYS_CHILDREN } from "store/constants";
import ManageFormCategoriesModal from "components/DynamicForms/shared/ManageFormCategoriesModal";
import SweetAlertConfirmationModal from "components/common/SweetAlertConfirmation/SweetAlertConfirmation";
import { apiPatchRequest } from "helpers/Requests";
import { getUrlRole } from "Utils/utils";
import { toast } from "react-toastify";
import FormScopeBadge from "components/DynamicForms/shared/FormScopeBadge";
import { getFormScopeMeta } from "components/DynamicForms/shared/formCategoryUtils";

const Forms = ({
  templateList,
  editFormTemplateHandler,
  viewFormTemplateHandler,
  isLoading,
  queryParams,
  formCategoriesList,
  handleFetch,
  getSortBy,
  paginationObj,
  setPaginationObj,
  routeRole,
  isUserGracePeriodOver,
  resetAndFetch,
  fetchFormCategories,
}) => {
  const [search, setSearch] = useState(null);
  const [showManageCategoriesModal, setShowManageCategoriesModal] =
    useState(false);
  const [manageCategoriesFocusCreate, setManageCategoriesFocusCreate] =
    useState(false);

  const openManageCategoriesModal = (focusCreate = false) => {
    setManageCategoriesFocusCreate(focusCreate);
    setShowManageCategoriesModal(true);
  };

  const closeManageCategoriesModal = () => {
    setShowManageCategoriesModal(false);
    setManageCategoriesFocusCreate(false);
  };
  const [sortBy, setSortBy] = useState(queryParams.sort_field);
  const [selectedFormCategories, setSelectedFormCategories] = useState([]);
  const [sortDescending, setSortDescending] = useState(
    queryParams.sort_order === SORT_TYPE.DESC ? true : false
  );
  const [renderLoading, setRenderLoading] = useState(LOADER_STATUS.NA);
  const [formTemplateStatus, setFormTemplateStatus] = useState(
    formTemplateStatusDefault
  );

  const [renderIsLoading, setRenderIsLoading] = useState(true);
  const initialFetchDoneRef = useRef(false);
  const [showToWorkerMap, setShowToWorkerMap] = useState({});
  const [pendingShowToWorker, setPendingShowToWorker] = useState(null);
  const [togglingShowToWorkerId, setTogglingShowToWorkerId] = useState(null);

  const navigate = useNavigate();
  const formScope = getFormScopeMeta(routeRole);
  const canManageCrmShowToWorker =
    routeRole === "crm" &&
    roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]) &&
    roleAccess(
      [],
      PERMISSION_KEYS.CRM_FORM,
      PERMISSION_KEYS_CHILDREN.CRM_FORM.UPDATE
    );
  const showCrmShowToWorkerColumn =
    canManageCrmShowToWorker &&
    formTemplateStatus?.value !== "draft";

  useEffect(() => {
    getSortBy(sortBy);
  }, [sortBy]);

  useEffect(() => {
    const nextMap = {};
    (templateList || []).forEach((template) => {
      nextMap[template.id] = !!template.show_to_worker;
    });
    setShowToWorkerMap(nextMap);
  }, [templateList]);

  useEffect(() => {
    setSortBy(queryParams.sort_field ? queryParams.sort_field : null);
    if (queryParams.sort_order) {
      setSortDescending(
        queryParams.sort_order
          ? queryParams.sort_order === SORT_TYPE.DESC
            ? true
            : false
          : false
      );
    }
    if (queryParams.hasOwnProperty("filter") && queryParams.filter !== null) {
      const categories = queryParams.filter.form_category;
      setSelectedFormCategories(
        Array.isArray(categories) && categories.length > 0 ? categories : []
      );
    }
    if (queryParams.search) {
      setSearch(queryParams.search);
    }
  }, [queryParams]);

  useEffect(() => {
    if (!queryParams.status) {
      return;
    }
    const nextStatus = formTemplateStatusOptions.find(
      (option) => option.value === queryParams.status
    );
    if (!nextStatus) {
      return;
    }
    setFormTemplateStatus((current) =>
      current?.value === nextStatus.value ? current : nextStatus
    );
  }, [queryParams.status]);

  useEffect(() => {
    if (initialFetchDoneRef.current || isLoading) {
      return;
    }

    setRenderLoading(LOADER_STATUS.PENDING);
    handleFetch(
      {
        status: formTemplateStatus.value,
        form_category: [],
        search: search ?? null,
        page: 1,
      },
      sortBy
    );
    initialFetchDoneRef.current = true;
  }, [isLoading]);

  useEffect(() => {
    if (!initialFetchDoneRef.current) {
      return;
    }

    setRenderLoading(LOADER_STATUS.PENDING);
    const timeOutId = setTimeout(() => {
      if (!isLoading) {
        const payload = {
          status: formTemplateStatus.value,
          search: search,
          page: 1,
        };

        if (selectedFormCategories.length > 0) {
          payload.form_category = selectedFormCategories;
        }

        handleFetch(payload, sortBy);
      }
    }, 1000);

    return () => clearTimeout(timeOutId);
  }, [search]);

  useEffect(() => {
    if (!isLoading && renderLoading === LOADER_STATUS.PENDING) {
      setRenderLoading(LOADER_STATUS.COMPLATE);
      setRenderIsLoading(false);
    }
  }, [isLoading]);

  const flipSort = (by) => {
    setSortBy(by);
    setSortDescending(!sortDescending);
    handleFetch({
      status: formTemplateStatus.value,
      sort_field: by,
      sort_order: !sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC,
    });
  };

  const applySystemWideShowToWorker = async (template, nextValue) => {
    setTogglingShowToWorkerId(template.id);
    try {
      const rolePrefix = getUrlRole();
      await apiPatchRequest(
        `/${rolePrefix}/form-templates/show-to-worker/${template.uuid}`,
        { show_to_worker: nextValue }
      );
      template.show_to_worker = nextValue;
      setShowToWorkerMap((prev) => ({
        ...prev,
        [template.id]: nextValue,
      }));
      toast.success(
        nextValue
          ? "Show to Worker enabled for all participants."
          : "Show to Worker disabled for all participants."
      );
    } catch (err) {
      toast.error(
        err?.message || "Something went wrong while updating Show to Worker."
      );
    } finally {
      setTogglingShowToWorkerId(null);
    }
  };

  return (
    <>
      <div className='form-admin-page bg-white'>
        <Header
          title='Forms'
          titleBadge={<FormScopeBadge scope={routeRole} />}
          desc={formScope.subtitle}
          {...(roleAccess([
            USER_ROLE.Super_Admin,
            USER_ROLE.Sp_Admin,
            USER_ROLE.Sp_manager,
          ]) === true &&
            roleAccess(
              [],
              routeRole === "hrm"
                ? PERMISSION_KEYS.HRM_FORM
                : PERMISSION_KEYS.CRM_FORM,
              routeRole === "hrm"
                ? PERMISSION_KEYS_CHILDREN.HRM_FORM.CREATE
                : PERMISSION_KEYS_CHILDREN.CRM_FORM.CREATE
            ) && {
              handleClick: () =>
                navigate(
                  routeRole === "hrm"
                    ? "/hrm/create-new-report"
                    : "/crm/create-new-report"
                ),
              btnLabel: `Create Form ${routeRole.toUpperCase()}`,
              icon: <BiPlus />,
              isButton: true,
            })}
        />

        <div className='form-admin-toolbar-wrap sm:px-6 lg:px-8 bg-white'>
          <div className='form-admin-toolbar'>
            <div className='form-admin-filter-field form-admin-filter-field--category'>
              <span className='form-admin-filter-label'>Category</span>
              <div className='form-admin-category-control'>
                <div className='form-admin-category-select'>
                  <SelectOption
                    cssClass='z-20'
                    placeHolder='All Categories'
                    isDisabled={isLoading || renderIsLoading}
                    isSearch={true}
                    selectedOption={selectedFormCategories}
                    handleChange={(e) => {
                      const nextSelection = Array.isArray(e) ? e : [];
                      handleFetch(
                        {
                          status: formTemplateStatus.value,
                          form_category: nextSelection,
                          page: 1,
                        },
                        sortBy
                      );
                      setSelectedFormCategories(
                        nextSelection.map((some) => ({
                          value: some.id,
                          label: some.label ?? some.name,
                          id: some.id,
                        }))
                      );
                    }}
                    data={formCategoriesList}
                    isMulti={true}
                  />
                </div>
                {roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]) && (
                  <div
                    className='form-admin-category-quick-actions'
                    role='group'
                    aria-label='Category management'
                  >
                    <button
                      type='button'
                      className='form-admin-category-icon-btn form-admin-category-icon-btn--primary'
                      onClick={() => openManageCategoriesModal(true)}
                      title='Create a new category'
                      aria-label='Create a new category'
                    >
                      <BiPlus aria-hidden='true' />
                    </button>
                    <button
                      type='button'
                      className='form-admin-category-icon-btn'
                      onClick={() => openManageCategoriesModal(false)}
                      title='Hide or show categories in this list'
                      aria-label='Manage category visibility'
                    >
                      <HiOutlineAdjustmentsHorizontal aria-hidden='true' />
                    </button>
                  </div>
                )}
              </div>
            </div>
            {roleAccess([
              USER_ROLE.Super_Admin,
              USER_ROLE.Sp_Admin,
              USER_ROLE.Sp_manager,
            ]) && (
              <div className='form-admin-filter-field form-admin-filter-field--status'>
                <span className='form-admin-filter-label'>Status</span>
                <SelectOption
                  isDisabled={isLoading || renderIsLoading}
                  isSearch={false}
                  selectedOption={formTemplateStatus}
                  handleChange={(e) => {
                    setFormTemplateStatus(e);
                    resetAndFetch({ status: e.value });
                  }}
                  data={formTemplateStatusOptions}
                  cssClass='z-20'
                />
              </div>
            )}
            <div className='form-admin-toolbar-search'>
              <span className='form-admin-filter-label'>Search</span>
              <Search
                setSearch={setSearch}
                search={search}
                width='w-full'
                placeholder='Search forms'
              />
            </div>
          </div>
        </div>
        <div className='flex flex-col sm:px-6 lg:px-8 pb-6 w-full'>
          <div className='-my-2 overflow-x-auto custom-scroll'>
            <div className='inline-block min-w-full py-2 align-middle'>
              <div className='form-admin-table-card'>
                {isLoading || renderIsLoading ? (
                  <div
                    className='flex flex-col w-full min-h-[320px] form-page items-center justify-center'
                  >
                    <CircularLoader
                      classes='flex justify-center items-center'
                      classloader='loader-l'
                    />
                  </div>
                ) : (
                  <table className='form-table min-w-full'>
                    <thead>
                      <tr>
                        <TableHeader
                          sortName='title'
                          flipSort={flipSort}
                          sortBy={sortBy}
                          sortDescending={sortDescending}
                          label='Title'
                          cssClass='px-3 py-3.5'
                        />
                        <TableHeader
                          isSort={false}
                          flipSort={flipSort}
                          sortBy={sortBy}
                          sortDescending={sortDescending}
                          label='Category'
                          cssClass='px-3 py-3.5'
                        />
                        <TableHeader
                          isSort={false}
                          flipSort={flipSort}
                          sortBy={sortBy}
                          sortDescending={sortDescending}
                          label='Subcategory'
                          cssClass='px-3 py-3.5'
                        />
                        {roleAccess([USER_ROLE.Super_Admin]) && (
                          <TableHeader
                            isSort={false}
                            flipSort={flipSort}
                            sortBy={sortBy}
                            sortDescending={sortDescending}
                            label='Form Status'
                            cssClass='px-3 py-3.5'
                          />
                        )}
                        {showCrmShowToWorkerColumn && (
                          <TableHeader
                            isSort={false}
                            flipSort={flipSort}
                            sortBy={sortBy}
                            sortDescending={sortDescending}
                            label='Show to Worker'
                            cssClass='px-3 py-3.5'
                            innerCssClass='justify-center'
                          />
                        )}
                        <TableHeader
                          isSort={false}
                          flipSort={flipSort}
                          sortBy={sortBy}
                          sortDescending={sortDescending}
                          label='Action'
                          cssClass='form-table-actions-col px-3 py-3.5'
                          innerCssClass='justify-center'
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {templateList &&
                        templateList
                          .sort((a, b) => a.title.localeCompare(b.title))
                          .map((template) => (
                            <tr key={template.id}>
                              <td className='whitespace-nowrap px-3 text-sm'>
                                <div className='capitalize font-medium text-gray-900'>
                                  {template.title}
                                </div>
                              </td>
                              <td className='whitespace-nowrap px-3 text-sm text-gray-500'>
                                <div className='text-gray-900 capitalize'>
                                  {template.category_name}
                                </div>
                              </td>
                              <td className='whitespace-nowrap px-3 text-sm text-gray-500'>
                                {template.sub_category_name ? (
                                  <div className='text-gray-900 capitalize'>
                                    {template.sub_category_name}
                                  </div>
                                ) : (
                                  <span className='form-table-empty-cell'>—</span>
                                )}
                              </td>
                              {roleAccess([USER_ROLE.Super_Admin]) && (
                                <td className='whitespace-nowrap px-3 text-sm'>
                                  <span
                                    className={`form-status-badge ${
                                      template.form_status === "publish"
                                        ? "is-published"
                                        : template.form_status === "hidden"
                                        ? "is-hidden"
                                        : "is-draft"
                                    }`}
                                  >
                                    {template.form_status === "publish"
                                      ? "Published"
                                      : template.form_status === "hidden"
                                      ? "Hidden"
                                      : "Draft"}
                                  </span>
                                </td>
                              )}
                              {showCrmShowToWorkerColumn && (
                                <td className='whitespace-nowrap px-3 py-4 text-center'>
                                  <label
                                    title={
                                      template.form_status === "draft"
                                        ? "Publish the form before changing Show to Worker"
                                        : "Applies this setting to all participants"
                                    }
                                    className={`relative inline-flex items-center ${
                                      template.form_status === "draft" ||
                                      togglingShowToWorkerId === template.id ||
                                      isUserGracePeriodOver
                                        ? "cursor-not-allowed opacity-60"
                                        : "cursor-pointer"
                                    }`}
                                  >
                                    <input
                                      type='checkbox'
                                      className='sr-only peer'
                                      disabled={
                                        template.form_status === "draft" ||
                                        togglingShowToWorkerId === template.id ||
                                        isUserGracePeriodOver
                                      }
                                      checked={!!showToWorkerMap[template.id]}
                                      onChange={(e) => {
                                        if (template.form_status === "draft") {
                                          return;
                                        }
                                        setPendingShowToWorker({
                                          template,
                                          nextValue: e.target.checked,
                                        });
                                      }}
                                    />
                                    <div
                                      className="form-admin-toggle w-10 h-5 rounded-full peer peer-checked:bg-blue-600
                                                  after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                                                  after:w-4 after:h-4 after:bg-white after:rounded-full after:shadow-sm
                                                  after:transition-all peer-checked:after:translate-x-5"
                                    ></div>
                                  </label>
                                </td>
                              )}
                              <td className='form-table-actions-col'>
                                <FormsListActionButtons
                                  editFormTemplateHandler={
                                    editFormTemplateHandler
                                  }
                                  viewFormTemplateHandler={
                                    viewFormTemplateHandler
                                  }
                                  formTemplateStatus={formTemplateStatus}
                                  template={template}
                                  routeRole={routeRole}
                                  isUserGracePeriodOver={isUserGracePeriodOver}
                                  handleFetch={handleFetch}
                                  selectedFormCategories={selectedFormCategories}
                                />
                              </td>
                            </tr>
                          ))}
                      {renderLoading === LOADER_STATUS.COMPLATE &&
                        templateList?.length === 0 && (
                          <tr>
                            <td
                              colSpan={8}
                              className='py-6 text-center text-red-600'
                            >
                              No data found !
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
{formCategoriesList &&
  formCategoriesList.length > 0 &&
  templateList?.length > 0 &&
  paginationObj.total > 0 && (
    <div className='bg-white sm:px-6 lg:px-8 w-full pb-10'>
      <PaginationComponent
        page={paginationObj.page}
        activeListLength={templateList}
        limit={ALL_FORM_LIST_LIMIT}
        totalList={paginationObj.total}
        handlePagination={(e) => {
          const obj = { ...paginationObj, page: e };
          setPaginationObj(obj);
          handleFetch({ page: e, status: formTemplateStatus.value }, sortBy);
        }}
        totalPages={paginationObj.totalPages || 0}
      />
    </div>
  )}

      <ManageFormCategoriesModal
        isOpen={showManageCategoriesModal}
        onClose={closeManageCategoriesModal}
        routeRole={routeRole}
        focusCreate={manageCategoriesFocusCreate}
        onUpdated={() => {
          if (fetchFormCategories) {
            fetchFormCategories({ role: routeRole });
          }
          setSelectedFormCategories([]);
          resetAndFetch({
            status: formTemplateStatus.value,
            form_category: [],
          });
        }}
      />
      {pendingShowToWorker && (
        <SweetAlertConfirmationModal
          isOpen={!!pendingShowToWorker}
          dangerMode={true}
          title='Overwrite participant settings?'
          content='Changing Show to Worker for this form will overwrite any participant-level customizations already made for this form across all participants. This cannot be undone. Do you want to continue?'
          setIsConfirm={(confirmed) => {
            const pending = pendingShowToWorker;
            setPendingShowToWorker(null);
            if (confirmed && pending) {
              applySystemWideShowToWorker(pending.template, pending.nextValue);
            }
          }}
        />
      )}
    </>
  );
};

export default Forms;
